
import { Vue, Component, Prop, Watch, Model } from 'vue-property-decorator';
import { Table, Pagination } from 'ant-design-vue';
import PaginationDatatable from '../models/PaginationDatatable';
import SorterDatatable from '../models/SorterDatatable';
import MetaDatatable from '../models/MetaDatatable';
import HeaderDatatable from '../models/HeaderDatatable';
import cloneDeep from 'lodash/cloneDeep';
import union from 'lodash/union';
import { differenceBetweenObjects } from '@/utils/differenceBetweenObjects';
import SearchFilter from '@/components/SearchFilter.vue';

@Component({})
export default class Datatable extends Vue {
    @Prop() columns!: HeaderDatatable[];
    @Model('update:meta') readonly meta!: MetaDatatable;
    @Prop() fetch!: (meta: MetaDatatable) => Promise<any>;
    @Prop({ default: false, type: Boolean }) hideSearch!: boolean;
    @Prop({ default: false, type: Boolean }) hidePerPage!: boolean;
    @Prop({ default: 'Rechercher...' }) searchPlaceholder!: string;
    @Prop({ default: null }) namespace!: null | string;
    @Prop({ default: null }) scroll!: null | { x?: number | true; y?: number };
    @Prop({
        default: () => {
            const meta = {
                page: 1,
                perPage: 20,
                search: null,
                orderBy: null,
                orderWay: null,
                filters: null,
            };
            return meta;
        },
    })
    readonly defaultMeta!: MetaDatatable;

    data: any[] = [];
    loading: boolean = false;
    bgLoading: boolean = false;
    locale = {
        emptyText: `Aucune donnée n'a été trouvée`,
    };

    pMeta: MetaDatatable | null = null;
    private requestId = 0;

    async created() {
        this.onMeta();
        this.restoreConf();
        await this.fetchData();
    }

    @Watch('meta', { deep: true })
    onMeta() {
        this.pMeta = this.meta;
    }

    handleTableChange(
        pagination: PaginationDatatable,
        filters: any,
        sorter: SorterDatatable
    ) {
        if (!this.pMeta) {
            return;
        }
        this.pMeta.page = pagination.current || 1;
        this.pMeta.orderBy = sorter.field || null;
        this.pMeta.orderWay = sorter.order === 'ascend' ? 'ASC' : 'DESC';
    }

    oldMeta: MetaDatatable | null = this.pMeta;
    @Watch('pMeta', { deep: true })
    onPMeta(newMeta: MetaDatatable) {
        this.$emit('update:meta', newMeta);
        if (!this.oldMeta) {
            this.oldMeta = cloneDeep(newMeta);
            return;
        }

        const oldMeta = this.oldMeta;
        this.oldMeta = cloneDeep(newMeta);

        const diff = differenceBetweenObjects(newMeta as any, oldMeta as any);
        const diffKeys = Object.keys(diff);

        const ignoredMetas = ['total', 'pages'];
        const fusion = union(diffKeys, ignoredMetas);

        if (fusion.length === ignoredMetas.length) {
            return;
        }

        // si et seulement si page <= 0
        if (
            fusion.length === 3 &&
            fusion.includes('page') &&
            newMeta.page <= 0
        ) {
            return;
        }

        // si et seulement si page 0 => 1
        if (
            fusion.length === 3 &&
            fusion.includes('page') &&
            oldMeta.page <= 0 &&
            newMeta.page === 1
        ) {
            return;
        }

        const meta: MetaDatatable = {
            filters: newMeta.filters,
            orderBy: newMeta.orderBy,
            orderWay: newMeta.orderWay,
            page: newMeta.page,
            perPage: newMeta.perPage,
            search: newMeta.search,
            total: -1,
            pages: -1,
        };

        this.saveConf(meta);
        this.fetchData();
    }

    async fetchData() {
        if (!this.pMeta) {
            return;
        }
        this.loading = true;
        this.bgLoading = false; // clear any leftover bg indicator from a previous progressive fetch
        const currentId = ++this.requestId;

        try {
            const result = await this.fetch(this.pMeta);

            if (currentId !== this.requestId) return;

            // Progressive mode: fetch returned { quick: Promise, full: Promise }
            if (result && typeof result === 'object' && 'quick' in result && 'full' in result) {
                const quickData = await result.quick;
                if (currentId !== this.requestId) return;

                this.data = quickData.data;
                this.pMeta.total = quickData.meta.total;
                this.pMeta.pages = quickData.meta.pages;
                this.loading = false;
                this.bgLoading = true;

                const fullData = await result.full;
                if (currentId !== this.requestId) { this.bgLoading = false; return; }

                this.data = fullData.data;
                this.bgLoading = false;
            } else {
                this.applyPageData(result);
                this.loading = false;
                this.bgLoading = false;
            }
        } catch (e) {
            if (currentId !== this.requestId) return;
            this.loading = false;
            this.bgLoading = false;
            throw e;
        }
    }

    applyPageData(pageData: any) {
        if (!this.pMeta) return;
        this.data = pageData.data;
        this.pMeta.total = pageData.meta.total;
        this.pMeta.pages = pageData.meta.pages;

        const pageMax = Math.ceil(this.pMeta.total / this.pMeta.perPage);
        if (this.pMeta.page > pageMax) {
            this.pMeta.page = pageMax;
        }
        if (pageData.meta.total > 0 && this.pMeta.page <= 0) {
            this.pMeta.page = 1;
        }
    }

    refresh() {
        this.fetchData();
    }

    saveConf(meta: MetaDatatable) {
        if (!this.namespace) {
            return;
        }

        const metaStr = JSON.stringify(meta);
        localStorage.setItem(`datatable.${this.namespace}`, metaStr);
    }

    restoreConf() {
        if (!this.pMeta || !this.namespace) {
            return;
        }

        const metaStr = localStorage.getItem(`datatable.${this.namespace}`);
        if (metaStr) {
            const meta: MetaDatatable = JSON.parse(metaStr);

            this.pMeta = {
                ...this.pMeta,
                filters: meta.filters,
                orderBy: meta.orderBy,
                orderWay: meta.orderWay,
                page: meta.page,
                perPage: meta.perPage,
                search: meta.search,
            };
            this.$emit('update:meta', this.pMeta);

            if (meta.orderBy) {
                const column = this.columns.find(
                    x => x.dataIndex === meta.orderBy
                );
                if (column) {
                    column.defaultSortOrder =
                        meta.orderWay === 'ASC' ? 'ascend' : 'descend';
                }
            }
        }
    }

    async reset() {
        this.$emit('update:meta', {
            ...this.pMeta,
            ...cloneDeep(this.defaultMeta),
        });

        /**
         * Petit hack pour réinitialiser les sorter de ant design
         */
        this.columns.forEach(column => {
            if (column.defaultSortOrder) {
                column.sortOrder = column.defaultSortOrder;
            } else {
                column.sortOrder = false;
            }
        });
        await this.$nextTick();
        this.columns.forEach(column => {
            delete column.sortOrder;
        });
    }

    // @ts-ignore
    render(h) {
        const searchFilter = h(SearchFilter, {
            props: {
                meta: this.pMeta,
                placeholder: this.searchPlaceholder,
            },
            on: {
                // meta.sync
                'update:meta': (meta: MetaDatatable) => {
                    if (this.pMeta) {
                        this.pMeta.search = meta.search;
                    }
                },
            },
        });

        const slotHeaderAction = h('div', this.$slots['header-action']);
        const title = h('div', this.$slots['header-title']);

        const table = h(Table, {
            class: 'datatable',
            props: {
                rowKey: (record: any) => record.id,
                dataSource: this.data,
                loading: this.loading,
                columns: this.columns,
                pagination: false,
                locale: this.locale,
                scroll: this.scroll || {},
            },
            on: {
                change: this.handleTableChange,
            },
            // @ts-ignore
            scopedSlots: this.$vnode.data.scopedSlots,
        });

        const pagination = h(Pagination, {
            style: 'margin: 16px 0',
            props: {
                defaultCurrent: 1,
                showTotal: (total: number, range: number[]) => {
                    const first = Math.max(0, range[0]);
                    return `${first}-${range[1]} de ${total} entrées`;
                },
                total: this.pMeta && Number(this.pMeta.total),
                showSizeChanger: !this.hidePerPage,
                pageSize: this.pMeta && this.pMeta.perPage,
                pageSizeOptions: ['5', '10', '20', '50', '100'],
                // v-model
                current: this.pMeta && this.pMeta.page,
            },
            on: {
                // v-model
                'change.current': (value: number) => {
                    if (this.pMeta) {
                        this.pMeta.page = value;
                    }
                },
                // pageSize.sync
                'update:pageSize': (value: number) => {
                    if (this.pMeta) {
                        this.pMeta.perPage = value;
                    }
                },
            },
        });

        const bgBar = this.bgLoading
            ? h('div', { class: 'bg-loading-bar' })
            : null;

        return (
            <div>
                <div class="actions">
                    <div class="left">
                        {title}
                        {this.hideSearch ? <span></span> : searchFilter}
                    </div>
                    <div class="slots">{slotHeaderAction}</div>
                </div>
                {bgBar}
                {table}
                <div class="pagination-container">
                    <a-button type="dashed" onclick={this.reset}>
                        <a-icon type="undo" />
                        Réinitialiser
                    </a-button>
                    {pagination}
                </div>
            </div>
        );
    }
}
